import type { CSSProperties } from 'react';

/**
 * Stała barwa encji (klienta) w etykietach (lifting 17.10.2026, DECISIONS „Zadania łatwiejsze w użyciu”):
 * paleta 10 spokojnych barw z tokenów `--entity-<barwa>-bg` / `--entity-<barwa>-fg` (styles/tokens.css; jasny
 * i ciemny motyw, para tło + tekst ≥ 4,5:1 — contrast.test.ts). Ten sam klient ma zawsze tę samą barwę na liście,
 * tablicy, karcie i w profilu.
 *
 * Wybór barwy:
 *  1. klient ma własny kolor (pole „Kolor” w karcie klienta, `clients.color`) → najbliższa barwa palety po odcieniu
 *     (np. HelpMyCar `#E8466B` → rose), żeby etykieta zgadzała się z kropką klienta w innych miejscach;
 *  2. bez koloru → barwa z identyfikatora (skrót FNV-1a; stabilny między sesjami i urządzeniami).
 * Szare kolory (bez odcienia) traktujemy jak brak koloru.
 */

export const ENTITY_TONES = [
  'blue',
  'violet',
  'plum',
  'rose',
  'clay',
  'sand',
  'olive',
  'green',
  'teal',
  'sky',
] as const;
export type EntityTone = (typeof ENTITY_TONES)[number];

/** Środkowy odcień barwy (stopnie) — do dopasowania własnego koloru klienta. */
const TONE_HUES: Record<EntityTone, number> = {
  blue: 230,
  violet: 262,
  plum: 300,
  rose: 340,
  clay: 16,
  sand: 34,
  olive: 66,
  green: 146,
  teal: 178,
  sky: 204,
};

/** FNV-1a 32-bit (bez zależności; wystarczy do równego rozkładu identyfikatorów UUID). */
export function hashString(value: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Odcień (0–360) koloru `#RRGGBB`; `null`, gdy to nie kolor hex albo kolor jest szary (bez odcienia). */
export function hueOf(hex: string): number | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m?.[1]) return null;
  const n = Number.parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d < 0.08) return null;
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return (h * 60 + 360) % 360;
}

function hueDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/** Barwa palety najbliższa kolorowi (po odcieniu); `null` dla koloru bez odcienia. */
export function nearestTone(hex: string): EntityTone | null {
  const hue = hueOf(hex);
  if (hue === null) return null;
  let best: EntityTone = ENTITY_TONES[0];
  let bestDist = Number.POSITIVE_INFINITY;
  for (const tone of ENTITY_TONES) {
    const dist = hueDistance(hue, TONE_HUES[tone]);
    if (dist < bestDist) {
      best = tone;
      bestDist = dist;
    }
  }
  return best;
}

/** Barwa encji: własny kolor (najbliższa barwa palety) albo skrót identyfikatora. */
export function entityTone(id: string, color?: string | null): EntityTone {
  const fromColor = color ? nearestTone(color) : null;
  if (fromColor) return fromColor;
  return ENTITY_TONES[hashString(id) % ENTITY_TONES.length] as EntityTone;
}

/** Styl etykiety w barwie (tło + tekst z tokenów; motyw przełącza się sam przez `data-theme`). */
export function entityStyle(tone: EntityTone): CSSProperties {
  return { backgroundColor: `var(--entity-${tone}-bg)`, color: `var(--entity-${tone}-fg)` };
}
